import { Suspense } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import PrivateRoute from "./PrivateRouting";
import ProtectedLoginRoute from "./ProtectedLoginRoute";
import AccessGuard from "./AccessGuard";
import RouteLoader from "./RouteLoader";
import { RoutesLink } from "@/utility/RouteLinks";

// Modular Route Configurations
import { protectedLoginRoutes, SsoLogin } from "./modules/auth.routes";
import { dashboardRoutes } from "./modules/dashboard.routes";
import { employeeRoutes } from "./modules/employee.routes";
import { attendanceRoutes } from "./modules/attendance.routes";
import { payrollRoutes } from "./modules/payroll.routes";
import { settingsRoutes } from "./modules/settings.routes";
import type { IPrivateRoutes } from "./types";

// Consolidated private routes across all domains
const privateRoutes: IPrivateRoutes[] = [
    ...dashboardRoutes,
    ...employeeRoutes,
    ...attendanceRoutes,
    ...payrollRoutes,
    ...settingsRoutes,
];

export default function CommonRouts() {
    const location = useLocation();

    return (
        <Suspense fallback={<RouteLoader />}>
            <Routes>
                {/* Protected Login Routes (Redirects to Dashboard if already logged in) */}
                {protectedLoginRoutes?.map((route, index) => (
                    <Route
                        key={`login-${index}`}
                        path={route.path}
                        element={<ProtectedLoginRoute element={route.route} />}
                    />
                ))}

                {/* All Private Routes — Protected by Auth & Fine-grained Role/Permission AccessGuard */}
                {privateRoutes?.map((route, index) => (
                    <Route
                        key={`private-${index}`}
                        path={route.path}
                        element={
                            <PrivateRoute
                                element={
                                    <AccessGuard
                                        element={route.route}
                                        accessKey={route.accessKey}
                                        teamAccess={route.teamAccess}
                                    />
                                }
                            />
                        }
                    />
                ))}

                {/* Public standalone route — accessible regardless of auth state */}
                <Route path={RoutesLink.authRoutes.ssoLogin} element={<SsoLogin />} />

                {/* Global Fallback */}
                <Route
                    path="*"
                    element={
                        <Navigate
                            to={{
                                pathname: RoutesLink.authRoutes.login,
                                search: location.search,
                            }}
                            replace
                        />
                    }
                />
            </Routes>
        </Suspense>
    );
}
